import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/lib/firebase'

/**
 * Invite codes. Cuplzap is invite-only: a code from an admin starts a household, and the other person
 * joins it through the household's email allowlist. Checking and using a code is the functions' job
 * (functions/src/invites.ts); the rules keep codes away from every client.
 */

export type InviteState = 'open' | 'used' | 'expired' | 'cancelled'

export interface InviteRow {
  code: string
  note: string | null
  state: InviteState
  createdAt: number
  expiresAt: number
  usedAt: number | null
  householdName: string | null
}

export interface StartInput {
  code: string
  name: string
  otherEmail: string
  timeZone: string
  displayName: string
}

const callStart = httpsCallable<StartInput, { householdId: string }>(functions, 'startHousehold')
const callCreate = httpsCallable<{ note: string }, { code: string; expiresAt: number }>(functions, 'createInvite')
const callList = httpsCallable<void, { invites: InviteRow[] }>(functions, 'listInvites')
const callCancel = httpsCallable<{ code: string }, { ok: boolean }>(functions, 'cancelInvite')

/** The function's own words for a refusal, without the HTTP status some transports tack on ("… [400]"). */
async function plainly<T>(call: Promise<T>): Promise<T> {
  try {
    return await call
  } catch (err) {
    throw new Error(((err as Error).message || 'Something went wrong. Try again.').replace(/\s*\[\d{3}\]$/, ''))
  }
}

/** Starts a household against a code and returns its id; throws with the function's own words when refused. */
export async function startHousehold(input: StartInput): Promise<string> {
  return (await plainly(callStart(input))).data.householdId
}

export async function createInvite(note: string): Promise<{ code: string; expiresAt: number }> {
  return (await plainly(callCreate({ note }))).data
}

export async function listInvites(): Promise<InviteRow[]> {
  return (await plainly(callList())).data.invites
}

export async function cancelInvite(code: string): Promise<void> {
  await plainly(callCancel({ code }))
}

/** Whether this person may make invite codes: admins/{uid} exists. */
export async function isAdmin(uid: string): Promise<boolean> {
  try {
    return (await getDoc(doc(db, 'admins', uid))).exists()
  } catch {
    return false
  }
}

/** "K7M4QX" as "K7M-4QX", the way it is shown and read out. */
export function formatCode(code: string): string {
  return `${code.slice(0, 3)}-${code.slice(3)}`
}

export function inviteLink(code: string): string {
  return `${window.location.origin}/?invite=${code}`
}

const KEY = 'cuplzap.invite'

/**
 * An invite link (my.cuplzap.com/?invite=K7M4QX) opens the sign-in screen first, so the code is kept for
 * the visit and offered on the welcome screen once the person is signed in. The address loses it.
 */
export function captureInviteFromUrl(): void {
  const url = new URL(window.location.href)
  const code = url.searchParams.get('invite')
  if (!code) return
  try {
    sessionStorage.setItem(KEY, code)
  } catch {
    // private mode: the code is typed instead
  }
  url.searchParams.delete('invite')
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

export function pendingInvite(): string {
  try {
    return sessionStorage.getItem(KEY) ?? ''
  } catch {
    return ''
  }
}

export function clearPendingInvite(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // nothing kept
  }
}
